import { LoadingButton } from '@mui/lab';
import { Typography, Tooltip, Divider, ButtonGroup, IconButton, Button, Grid, Box, Stack } from '@mui/material';
import GhostDialog from 'components/dialog/GhostDialog';
import { FormikProps, Formik } from 'formik';
import { useRef, useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router';
import { ImageViwerNavigateFile } from 'types/image-viewer';
import { VerifiactionFileDetailsTypes, VerificationPatternsListTypes, VerificationBatchListTypes } from 'types/verification';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import DoneIcon from '@mui/icons-material/Done';
import Yup from 'utils/yup';
import { ArrowRightAltSharp, KeyboardArrowDownSharp, KeyboardArrowUpSharp, ArrowBackIos } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { FormField } from 'components';
import Autocomplete from 'components/forms/Autocomplete';
import InfoIcon from 'assets/icons/svg/info-icon.svg?react';
import { useTheme } from '@mui/material';
import { ThemeMode } from 'types/config';

const translate = (key: string) => (window as any).translate(key);

interface TitlebarProps {
  fileLevelDetails?: VerifiactionFileDetailsTypes;
  handleFileNavigation: ImageViwerNavigateFile;
  isSecondaryScreen?: boolean;
  patterns: VerificationPatternsListTypes[];
  selectedUser?: string;
  setSelectedUser?: any;
  finishAndMoveNextBatch: any;
  batchList: VerificationBatchListTypes[];
  selectedBatchId: number;
  isVerifiedBatch: boolean;
  openLeftPane?: () => void;
  openBottomPane?: () => void;
  disableBottomPane?: boolean;
  isOpenBottomPane?: boolean;
  closeBottomPane?: () => void;
}
export const Titlebar = ({
  fileLevelDetails,
  handleFileNavigation,
  isSecondaryScreen,
  patterns,
  selectedUser,
  setSelectedUser,
  finishAndMoveNextBatch,
  batchList,
  selectedBatchId,
  isVerifiedBatch,
  openLeftPane,
  openBottomPane,
  disableBottomPane,
  isOpenBottomPane = false,
  closeBottomPane = undefined
}: TitlebarProps) => {
  const theme = useTheme();
  const userFormRef = useRef<FormikProps<{ user: string }> | null>(null);
  const [openFinishForm, setOpenFinishForm] = useState<boolean>(false);
  const [isFinsihSubmitting, setIsFinsihSubmitting] = useState<boolean>(false);
  const location = useLocation();
  const navigate = useNavigate();
  const getShortFileName = (fileName: any) => {
    fileName = String(fileName);
    const maxLength = 20; // Adjust the length as needed
    return fileName.length > maxLength ? `${fileName.substring(0, maxLength)}...` : fileName;
  };

  const onCancelFinishForm = () => {
    setOpenFinishForm(false);
  };

  const onToggle = () => (isOpenBottomPane ? closeBottomPane?.() : openBottomPane?.());

  const usersList: string[] = useMemo(() => [...new Set<string>(patterns.map(({ userName }) => userName))], [patterns]);

  useEffect(() => {
    if (userFormRef.current) {
      let selectedOption: any;
      if (selectedUser === '-1') {
        selectedOption = { value: '-1', label: 'All User' };
      } else {
        selectedOption = { id: selectedUser, label: selectedUser };
      }
      userFormRef.current.setFieldValue('user', selectedOption);
    }
  }, [selectedUser, userFormRef]);

  const initiateVerifyProcess = async () => {
    try {
      setIsFinsihSubmitting(true);
      await finishAndMoveNextBatch(fileLevelDetails?.batchID, fileLevelDetails?.docType);
      setOpenFinishForm(false);
    } catch (e) {
      console.error(e);
    } finally {
      setIsFinsihSubmitting(false);
    }
  };

  const { isHavingPrevBatch, isHavingNextbatch }: { isHavingPrevBatch: boolean; isHavingNextbatch: boolean } = useMemo(() => {
    const data: { isHavingPrevBatch: boolean; isHavingNextbatch: boolean } = { isHavingPrevBatch: false, isHavingNextbatch: false };
    if (selectedBatchId) {
      const currentBatchIndex = batchList.findIndex(({ batchID }) => batchID === selectedBatchId);
      if (currentBatchIndex >= 0) {
        data.isHavingNextbatch = Boolean(batchList[currentBatchIndex + 1]);
        data.isHavingPrevBatch = Boolean(batchList[currentBatchIndex - 1]);
      }
    }
    return data;
  }, [batchList, selectedBatchId]);

  return (
    <Box className="configuration-title-bar" sx={{ position: 'relative' }}>
      <Box className="title-field-wrapper">
        {!isSecondaryScreen && (
          <Box>
            <Formik
              enableReinitialize
              initialValues={{ user: '-1' }}
              validationSchema={Yup.object().shape({
                user: Yup.string()
              })}
              onSubmit={() => undefined}
              innerRef={userFormRef}
            >
              {() => (
                <form>
                  <FormField
                    component={Autocomplete}
                    className="user-selection"
                    name="user"
                    label=""
                    options={[{ id: '-1', label: 'All User' }, ...usersList.map((userName) => ({ id: userName, label: userName }))]}
                    onChange={(option: any) => setSelectedUser && setSelectedUser(option.id)}
                    disableClearable
                    sx={{
                      backgroundColor:
                        theme.palette.mode === ThemeMode.LIGHT ? theme.palette.background.paper : theme.palette.background.default
                    }}
                  />
                </form>
              )}
            </Formik>
          </Box>
        )}

        <Box>
          <Typography className="title-field-label">{translate('pages.review.verification.labels.documentType')}</Typography>
          <Tooltip title={fileLevelDetails?.docType || 'Unknown'} arrow>
            <Box>
              <Typography className="title-field-value" noWrap>
                {getShortFileName(fileLevelDetails?.docType || 'Unknown')}
              </Typography>
            </Box>
          </Tooltip>
        </Box>
        <Box>
          <Typography className="title-field-label">{translate('pages.review.verification.labels.fileName')}</Typography>
          <Tooltip title={fileLevelDetails?.fileName || 'Unknown'} arrow>
            <Box>
              <Typography className="title-field-value" noWrap>
                {getShortFileName(fileLevelDetails?.fileName || 'Unknown')}
              </Typography>
            </Box>
          </Tooltip>
        </Box>
      </Box>

      {!isSecondaryScreen && (
        <>
          <Stack
            direction="row"
            gap="8px"
            alignItems="center"
            divider={<Divider orientation="vertical" flexItem className="image-viewer-titlebar-icon-button-divider" />}
            sx={{ position: 'sticky', right: 0 }}
          >
            <Button
              onClick={() => {
                navigate('/review/verification',{
                  state: {
                    batchId: location?.state.Dropdownselected  != null ? fileLevelDetails?.batchID.toString() : '',
                    docTypeID: location?.state.Dropdownselected  != null ?  fileLevelDetails?.docTypeId.toString() : '',
                    processID: location?.state.Dropdownselected != null ? fileLevelDetails?.processID.toString() : ''
                  }
                });
              }}
              size={!!openLeftPane ? 'small' : 'medium'}
              variant="outlined"
              className="globalBackButton"
              aria-label="Back"
              startIcon={<ArrowBackIos />}
              sx={{ backgroundColor: theme.palette.background.default }}
            >
              {translate('elements.buttons.back')}
            </Button>
            {!!openLeftPane && (
              <Tooltip placement="top" title={'Navigate to previous file '}>
                <IconButton onClick={openLeftPane} className="image-viewer-titlebar-icon-button">
                  <ArrowRightAltSharp color="primary" />
                </IconButton>
              </Tooltip>
            )}
            <ButtonGroup className="image-viewer-titlebar-icon-button-group" sx={{ backgroundColor: theme.palette.background.default }}>
              <Tooltip placement="top" title={'Navigate to previous file '}>
                <IconButton onClick={handleFileNavigation('PREV')} disabled={!isHavingPrevBatch}>
                  <KeyboardDoubleArrowLeftIcon {...(isHavingPrevBatch ? { color: 'primary' } : {})} />
                </IconButton>
              </Tooltip>
              <Tooltip placement="top" title={'Navigate to next file '}>
                <IconButton onClick={handleFileNavigation('NEXT')} disabled={!isHavingNextbatch}>
                  <KeyboardDoubleArrowRightIcon {...(isHavingNextbatch ? { color: 'primary' } : {})} />
                </IconButton>
              </Tooltip>
            </ButtonGroup>

            <Tooltip placement="top" title={'Click to initiate verification complete'}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<DoneIcon />}
                onClick={() => setOpenFinishForm(true)}
                disabled={isVerifiedBatch || location?.state?.status === 'CURATIONCOMPLETED'}
              >
                {translate('pages.review.verification.labels.finish')}
              </Button>
            </Tooltip>
            {!!openBottomPane && !!closeBottomPane && (
              <Tooltip placement="top" title={'Navigate to previous file '}>
                <IconButton onClick={onToggle} className="image-viewer-titlebar-icon-button">
                  {isOpenBottomPane ? <KeyboardArrowDownSharp color="primary" /> : <KeyboardArrowUpSharp color="primary" />}
                </IconButton>
              </Tooltip>
            )}
          </Stack>
          <GhostDialog className="image-viewer-finish-modal" open={openFinishForm} onClose={() => {}} customMaxWidth="500px">
            <Box className="move-to-exception-form-container" p="30px">
              <Grid container justifyContent="space-between" alignItems="center">
                <Grid item flex={1} marginRight="-20px">
                  <Typography textAlign="center" variant="h2" className="popup-title">
                    {translate('pages.review.verification.title')}
                  </Typography>
                </Grid>
                <Grid item>
                  <InfoIcon />
                </Grid>
              </Grid>
              <Divider sx={{ my: 1 }} className="popup-tittle-divider" />
              <Box mt={3}>
                <Stack gap={3}>
                  <Grid container alignItems={'center'} columnSpacing="30px">
                    <Grid item md={4}>
                      <Typography>{translate('pages.review.verification.labels.documentType')}</Typography>
                    </Grid>
                    <Grid item md={8}>
                      <Typography className="finish-file-modal-label">{fileLevelDetails?.docType}</Typography>
                    </Grid>
                  </Grid>
                  <Grid container alignItems={'center'} columnSpacing="30px">
                    <Grid item md={4}>
                      <Typography>{translate('pages.review.verification.labels.batchId')}</Typography>
                    </Grid>
                    <Grid item md={8}>
                      <Typography className="finish-file-modal-label">{fileLevelDetails?.batchID}</Typography>
                    </Grid>
                  </Grid>
                  <Typography textAlign="center" className="popup-description">
                    {translate('pages.review.verification.haveYouVerified')}
                  </Typography>
                </Stack>
              </Box>
            </Box>
            <Box p="30px" sx={{ backgroundColor: theme.palette.background.paper }}>
              <Grid container justifyContent="space-around" alignItems="center" columnSpacing="30px">
                <Grid item>
                  <Button variant="contained" className="buttonSecondary" onClick={onCancelFinishForm}>
                    {translate('elements.buttons.cancel').toUpperCase()}
                  </Button>
                </Grid>
                <Grid item>
                  <LoadingButton variant="contained" color="primary" loading={isFinsihSubmitting} onClick={initiateVerifyProcess}>
                    {translate('pages.review.verification.buttons.verified')}
                  </LoadingButton>
                </Grid>
              </Grid>
            </Box>
          </GhostDialog>
        </>
      )}
    </Box>
  );
};
